import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { parseYoutubeVideoId, youtubeEmbedSrc } from './youtubeHref';

export type YoutubePlayerHandle = {
  play: () => boolean;
  mute: () => void;
  unmute: () => void;
  toggleMute: () => boolean;
  ready: boolean;
  muted: boolean;
};

type YTPlayer = {
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
  unMute: () => void;
  isMuted?: () => boolean;
  destroy: () => void;
};

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: string | HTMLElement,
        opts: {
          videoId?: string;
          width?: string | number;
          height?: string | number;
          playerVars?: Record<string, string | number>;
          events?: {
            onReady?: (e: { target: YTPlayer }) => void;
            onError?: (e: { data: number }) => void;
          };
        }
      ) => YTPlayer;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiLoading: Promise<void> | null = null;

function loadYoutubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (apiLoading) return apiLoading;
  apiLoading = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      try {
        prev?.();
      } catch {
        // ignore
      }
      resolve();
    };
    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    } else if (window.YT?.Player) {
      resolve();
    }
  });
  return apiLoading;
}

type Props = {
  urlOrId: string;
  active: boolean;
  className?: string;
  /** Initial / controlled mute. Changing this must NOT remount the player. */
  muted?: boolean;
  autoplay?: boolean;
  controls?: boolean;
  onMutedChange?: (muted: boolean) => void;
};

/**
 * YouTube Live/VOD with playsinline.
 * Mute toggles call the YT API only — never destroy/recreate (avoids black flash).
 */
export const YoutubePlayer = forwardRef<YoutubePlayerHandle, Props>(function YoutubePlayer(
  {
    urlOrId,
    active,
    className,
    muted: mutedProp = true,
    autoplay = true,
    controls = false,
    onMutedChange,
  },
  ref
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const mutedRef = useRef(mutedProp);
  const onMutedChangeRef = useRef(onMutedChange);
  onMutedChangeRef.current = onMutedChange;
  const uid = useId().replace(/:/g, '');
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(mutedProp);
  const videoId = parseYoutubeVideoId(urlOrId);

  const applyMute = (next: boolean, notify: boolean) => {
    mutedRef.current = next;
    setMuted(next);
    try {
      if (next) playerRef.current?.mute();
      else playerRef.current?.unMute();
    } catch {
      // ignore
    }
    if (notify) onMutedChangeRef.current?.(next);
  };

  useImperativeHandle(
    ref,
    () => ({
      ready,
      muted,
      play: () => {
        const p = playerRef.current;
        if (!p?.playVideo) return false;
        try {
          if (mutedRef.current) p.mute();
          else p.unMute();
          p.playVideo();
          return true;
        } catch {
          return false;
        }
      },
      mute: () => applyMute(true, true),
      unmute: () => applyMute(false, true),
      toggleMute: () => {
        const next = !mutedRef.current;
        applyMute(next, true);
        try {
          playerRef.current?.playVideo?.();
        } catch {
          // ignore — caller may also call play()
        }
        return next;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable imperative API
    [ready, muted]
  );

  // Create / destroy player only when stream identity changes — never on mute toggle
  useEffect(() => {
    playerRef.current?.destroy?.();
    playerRef.current = null;
    setReady(false);

    if (!active || !videoId || !hostRef.current) {
      if (hostRef.current) hostRef.current.innerHTML = '';
      return;
    }

    let cancelled = false;
    const host = hostRef.current;
    host.innerHTML = '';
    const targetId = `il-yt-${uid}`;
    const target = document.createElement('div');
    target.id = targetId;
    target.className = 'il-yt-target';
    host.appendChild(target);

    const initialMuted = mutedRef.current;
    mutedRef.current = initialMuted;
    setMuted(initialMuted);

    loadYoutubeApi().then(() => {
      if (cancelled || !window.YT?.Player) {
        if (cancelled || !hostRef.current) return;
        hostRef.current.innerHTML = '';
        const iframe = document.createElement('iframe');
        iframe.className = 'il-yt-iframe';
        iframe.title = 'YouTube Live';
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
        iframe.allowFullscreen = true;
        iframe.src = youtubeEmbedSrc(videoId, {
          autoplay,
          mute: initialMuted,
          controls,
        });
        hostRef.current.appendChild(iframe);
        setReady(true);
        return;
      }

      playerRef.current = new window.YT.Player(targetId, {
        width: '100%',
        height: '100%',
        videoId,
        playerVars: {
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          autoplay: autoplay ? 1 : 0,
          mute: initialMuted ? 1 : 0,
          controls: controls ? 1 : 0,
          fs: 0,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            playerRef.current = e.target;
            try {
              if (mutedRef.current) e.target.mute();
              else e.target.unMute();
              if (autoplay) e.target.playVideo();
            } catch {
              // ignore
            }
            setReady(true);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {
        // ignore
      }
      playerRef.current = null;
      if (hostRef.current) hostRef.current.innerHTML = '';
    };
  }, [active, videoId, uid, autoplay, controls]);

  // External mute preference (e.g. shared mini/full state) — API only, no remount
  useEffect(() => {
    if (!ready) return;
    if (mutedProp === mutedRef.current) return;
    applyMute(mutedProp, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutedProp, ready]);

  if (!videoId) {
    return <div className={`il-yt-wrap ${className || ''}`.trim()} />;
  }

  return (
    <div className={`il-yt-wrap ${className || ''}`.trim()} data-muted={muted ? '1' : '0'}>
      <div ref={hostRef} className="il-yt-host" />
    </div>
  );
});
